<template>
  <div>
    <v-data-table
      :headers="computedHeaders"
      :items="customers"
      :options="tableOptions"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :footer-props="{
        itemsPerPageText: '行/ページ:',
        itemsPerPageOptions: [10, 25, 50],
      }"
      no-data-text="データがありません"
      loading-text="読込中..."
      class="customer-list-table"
      @click:row="onCustomerClick"
      @update:options="onOptionsUpdate"
    >
      <template #[`item.attributes.memberNumber`]="{ item }">
        {{ item.attributes.memberNumber || '-' }}
      </template>

      <template #[`item.attributes.name`]="{ item }">
        {{ item.attributes.name }}
      </template>

      <template #[`item.attributes.visitCount`]="{ item }">
        {{ item.attributes.visitCount || 0 }}
      </template>

      <template #[`item.attributes.lastVisitedAt`]="{ item }">
        {{ formatDate(item.attributes.lastVisitedAt) }}
      </template>

      <template #[`item.attributes.createdAt`]="{ item }">
        {{ formatDate(item.attributes.createdAt) }}
      </template>

      <!-- カスタムフィールド列 -->
      <template
        v-for="field in customFields"
        #[`item.customField_${field.id}`]="{ item }"
      >
        <span :key="field.id">{{ getCustomFieldValue(item, field) }}</span>
      </template>

      <template #[`item.actions`]>
        <v-icon small>
          {{ icons.mdiChevronRight }}
        </v-icon>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import { mdiChevronRight } from '@mdi/js'
import { format } from 'date-fns'

export default {
  name: 'CustomerList',
  props: {
    customers: {
      type: Array,
      default: () => [],
      required: true,
    },
    customFields: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 50,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const tableOptions = ref({
      page: props.page,
      itemsPerPage: props.perPage,
    })

    const serverItemsLength = computed(() => props.totalCount)

    const onOptionsUpdate = options => {
      tableOptions.value = options
      emit('update:pagination', {
        page: options.page,
        perPage: options.itemsPerPage,
      })
    }
    const baseHeaders = [
      {
        text: '会員番号',
        value: 'attributes.memberNumber',
        sortable: true,
        width: '100',
      },
      {
        text: '名前',
        value: 'attributes.name',
        sortable: true,
        width: '200',
      },
      {
        text: '来店回数',
        value: 'attributes.visitCount',
        sortable: true,
        width: '100',
      },
      {
        text: '最終来店日',
        value: 'attributes.lastVisitedAt',
        sortable: true,
        width: '180',
      },
      {
        text: '作成日時',
        value: 'attributes.createdAt',
        sortable: true,
        width: '180',
      },
    ]

    const actionsHeader = {
      text: '',
      value: 'actions',
      sortable: false,
      width: '40',
      align: 'end',
    }

    const computedHeaders = computed(() => {
      // カスタムフィールド列を生成
      const customFieldHeaders = props.customFields.map(field => ({
        text: field.name,
        value: `customField_${field.id}`,
        sortable: false,
        width: '120',
      }))

      return [...baseHeaders, ...customFieldHeaders, actionsHeader]
    })

    const formatDate = dateString => {
      if (!dateString) return '-'
      try {
        return format(new Date(dateString), 'yyyy/MM/dd HH:mm')
      } catch (e) {
        return dateString
      }
    }

    const getCustomFieldValue = (customer, field) => {
      const customFieldValues = customer.attributes?.customFieldValues?.data || []
      const valueObj = customFieldValues.find(
        v => String(v.attributes?.customerCustomFieldId) === String(field.id),
      )

      if (!valueObj) return '-'

      const { value, typedValue } = valueObj.attributes

      // boolean型の場合
      if (field.type === 'boolean') {
        return typedValue === true ? 'はい' : 'いいえ'
      }

      return value || '-'
    }

    const onCustomerClick = customer => {
      emit('click', customer)
    }

    return {
      tableOptions,
      serverItemsLength,
      computedHeaders,
      formatDate,
      getCustomFieldValue,
      onCustomerClick,
      onOptionsUpdate,
      icons: {
        mdiChevronRight,
      },
    }
  },
}
</script>

<style scoped>
/* 行をクリック可能にする */
::v-deep .v-data-table__wrapper tbody tr {
  cursor: pointer;
}

/* ホバー時のハイライト */
::v-deep .v-data-table__wrapper tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
